import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';

import { ChatMessage } from './ChatMessage.js';
import type { ProviderInfo } from './types.js';
import { useAiChat } from './useAiChat.js';

interface AIChatPanelProps {
  open: boolean;
  onClose: () => void;
}

export function AIChatPanel({ open, onClose }: AIChatPanelProps): JSX.Element | null {
  const { t } = useTranslation('ai');
  const location = useLocation();
  const canUse = usePermission('ai.use');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');

  const { messages, isStreaming, error, providers, send, cancel } = useAiChat({
    pageContext: { route: location.pathname },
  });

  useEffect(() => {
    if (open) {
      // Focus input shortly after open animation.
      const id = window.setTimeout(() => inputRef.current?.focus(), 100);
      return () => window.clearTimeout(id);
    }
    return;
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isStreaming]);

  if (!open) return null;

  const configuredProviders = (providers ?? []).filter((p) => p.configured);

  const onSubmit = async (): Promise<void> => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    await send(text, selectedProvider ? { provider: selectedProvider } : {});
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
    if (e.key === 'Escape') onClose();
  };

  if (!canUse) {
    return (
      <Drawer onClose={onClose}>
        <div className="p-6">
          <h2 className="text-lg font-semibold">{t('title')}</h2>
          <p className="mt-2 text-sm text-fg-muted">{t('forbidden')}</p>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer onClose={onClose}>
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h2 className="text-base font-semibold">{t('title')}</h2>
          <p className="text-xs text-fg-subtle">{t('subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-fg-subtle hover:bg-bg-muted"
          aria-label={t('close')}
        >
          <span aria-hidden>×</span>
        </button>
      </header>

      <div className="flex flex-1 flex-col gap-3 overflow-hidden">
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {messages.length === 0 ? (
            <EmptyState />
          ) : (
            messages.map((m) => <ChatMessage key={m.id} message={m} />)
          )}
          {error && (
            <div className="rounded-md border border-danger bg-danger/5 px-3 py-2 text-xs text-danger" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-bg-subtle px-4 py-3">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <label className="text-fg-subtle" htmlFor="ai-provider">{t('provider.label')}</label>
            <select
              id="ai-provider"
              className="rounded-md border border-border bg-bg px-2 py-1 text-xs"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <option value="">{t('provider.default')}</option>
              {(providers ?? []).map((p: ProviderInfo) => (
                <option key={p.name} value={p.name} disabled={!p.configured}>
                  {p.name}{p.configured ? '' : ` ${t('provider.notConfigured')}`}
                </option>
              ))}
            </select>
            {providers && configuredProviders.length === 0 && (
              <span className="ms-auto text-danger">{t('provider.noneConfigured')}</span>
            )}
          </div>
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('inputPlaceholder')}
              className="flex-1 resize-none rounded-md border border-border bg-bg px-3 py-2 text-sm"
              disabled={isStreaming || configuredProviders.length === 0}
            />
            {isStreaming ? (
              <Button variant="secondary" onClick={cancel}>{t('cancel')}</Button>
            ) : (
              <Button onClick={onSubmit} disabled={!input.trim() || configuredProviders.length === 0}>
                {t('send')}
              </Button>
            )}
          </div>
          <p className="mt-2 text-[0.7rem] text-fg-subtle">{t('shortcuts')}</p>
        </div>
      </div>
    </Drawer>
  );
}

function Drawer({ children, onClose }: { children: React.ReactNode; onClose: () => void }): JSX.Element {
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col bg-bg shadow-xl"
      >
        {children}
      </aside>
    </>
  );
}

function EmptyState(): JSX.Element {
  const { t } = useTranslation('ai');
  const examples = [
    t('examples.0'),
    t('examples.1'),
    t('examples.2'),
    t('examples.3'),
  ];
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <div className="text-3xl" aria-hidden>✨</div>
      <p className="text-sm text-fg-muted">{t('empty')}</p>
      <ul className="space-y-1 text-xs text-fg-subtle">
        {examples.map((ex, i) => (
          <li key={i} className="rounded-md bg-bg-muted px-2 py-1">"{ex}"</li>
        ))}
      </ul>
    </div>
  );
}
